import { supabase } from '../lib/supabase';
import { config } from '../lib/config';
import { BOOKING_WINDOW_DAYS } from '../lib/constants';
import type { FormOption, AvailableSlot, LeadInput, LeadPath } from '../types/forms';

// ── Public reads (anon, via SECURITY DEFINER RPCs) ───────────────────────────
export async function fetchFormOptions(): Promise<FormOption[]> {
  const { data, error } = await supabase.rpc('public_form_options');
  if (error) throw new Error(error.message);
  return (data ?? []) as FormOption[];
}

export async function fetchAvailableSlots(
  schoolId: string,
  courseId: string,
  days: number = BOOKING_WINDOW_DAYS,
): Promise<AvailableSlot[]> {
  const { data, error } = await supabase.rpc('public_available_slots', {
    p_school_id: schoolId,
    p_course_id: courseId,
    p_days: days,
  });
  if (error) throw new Error(error.message);
  return (data ?? []) as AvailableSlot[];
}

// ── Writes (via submit-lead edge function) ───────────────────────────────────
async function callFn<T>(payload: Record<string, unknown>): Promise<T> {
  const res = await fetch(`${config.functionsUrl}/submit-lead`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      apikey: config.supabaseAnonKey,
      Authorization: `Bearer ${config.supabaseAnonKey}`,
    },
    body: JSON.stringify(payload),
  });
  const out = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) throw new Error((out.error as string) ?? `Fehler ${res.status}`);
  return out as T;
}

export const submitCapture = (lead: LeadInput, turnstileToken: string) =>
  callFn<{ lead_id: string }>({
    action: 'capture',
    is_test: config.isTest,
    turnstile_token: turnstileToken,
    lead,
  });

export const selectPath = (
  leadId: string,
  path: LeadPath,
  extra?: Record<string, unknown>,
) =>
  callFn<{ ok: boolean; customer_id?: string; test?: boolean }>({
    action: 'select_path',
    lead_id: leadId,
    path,
    ...extra,
  });

export const bookSlot = (leadId: string, scheduleId: string, bookingDate: string) =>
  callFn<{ ok: boolean; booking_id?: string; customer_id?: string; replaced_count?: number }>({
    action: 'book',
    lead_id: leadId,
    schedule_id: scheduleId,
    booking_date: bookingDate,
  });

export type ConfirmState = 'confirmed' | 'already' | 'canceled' | 'not_found';

// Bestätigung einer Ferien-Vorbuchung über den Link aus der Erinnerungs-E-Mail.
export const confirmBooking = (token: string) =>
  callFn<{ ok: boolean; state: ConfirmState }>({
    action: 'confirm_booking',
    token,
  });

// ── Selbst-Stornierung eines Probetermins (Link aus Bestätigungs-/Erinnerungsmail) ──
export type CancelLookupState = 'ok' | 'not_active' | 'not_found';
export type CancelState = 'canceled' | 'already_canceled' | 'not_found';

export interface CancelBookingInfo {
  booking_date: string;
  school: string;
  sport: string;
}

// Termin-Eckdaten für die Storno-Seite (freundliche Anzeige, was storniert wird).
export const cancelLookup = (token: string) =>
  callFn<{ ok: boolean; state: CancelLookupState; booking?: CancelBookingInfo }>({
    action: 'cancel_lookup',
    token,
  });

// Führt die Stornierung durch. Pflicht: mindestens ein Grund ODER ein Kommentar.
export const cancelBooking = (token: string, reasons: string[], comment: string) =>
  callFn<{ ok: boolean; state: CancelState }>({
    action: 'cancel_booking',
    token,
    reasons,
    comment,
  });
